import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

/**
 * Android/iOS hardware (or gesture) back-button handler.
 *
 * Behaviour:
 *  - Sub-pages (anything not in ROOT_ROUTES) → navigate(-1).
 *  - Root tab pages (Discover, Matches, Chats, Dates, Profile, landing) →
 *    first back press shows a toast "Press back again to exit",
 *    second press within 2 seconds calls App.exitApp() (Android only;
 *    iOS ignores exitApp per Apple guidelines).
 *
 * No-op on web — browsers handle the back arrow natively.
 */
const ROOT_ROUTES = new Set<string>([
  '/',
  '/discover',
  '/matches',
  '/chat',           // chat list
  '/date-plan',      // dates tab landing
  '/profile',
  '/landing',
  '/auth',
]);

export function useAndroidBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastPressRef = useRef<number>(0);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        if (cancelled) return;
        const handle = await App.addListener('backButton', ({ canGoBack }) => {
          const pathname = window.location.pathname;
          const onRoot = ROOT_ROUTES.has(pathname);

          if (!onRoot && canGoBack) {
            navigate(-1);
            return;
          }

          // On root → double-tap to exit
          const now = Date.now();
          if (now - lastPressRef.current < 2000) {
            try { App.exitApp(); } catch { /* iOS no-op */ }
          } else {
            lastPressRef.current = now;
            toast('Press back again to exit', { duration: 2000 });
          }
        });
        cleanup = () => handle?.remove?.();
      } catch {
        /* @capacitor/app not available — web/dev fallback */
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [navigate, location.pathname]);
}
